/**
 * Step1Exposure — Exposição step of the RiskApplicationModal stepper.
 */

import React from 'react';
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  TextField,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import {
  ExposureLevel,
  ExposureLevelLabels,
  ExposureLevelColors,
} from '../../../types/riskApplication';
import type { StepperFormValues } from '../RiskApplicationModal';

interface Step1ExposureProps {
  control: Control<StepperFormValues>;
  errors: FieldErrors<StepperFormValues>;
  disabled: boolean;
}

interface ExposureLevelChipProps {
  level: ExposureLevel;
  selected?: boolean;
}

const ExposureLevelChip: React.FC<ExposureLevelChipProps> = ({ level, selected }) => (
  <Chip
    label={ExposureLevelLabels[level]}
    color={ExposureLevelColors[level]}
    variant={selected ? 'filled' : 'outlined'}
    size="small"
    sx={{ minWidth: 80 }}
  />
);

function getExposureLevelDescription(level: ExposureLevel): string {
  switch (level) {
    case ExposureLevel.LOW:
      return 'Exposicao ocasional ou em baixa concentracao';
    case ExposureLevel.MEDIUM:
      return 'Exposicao regular dentro dos limites de tolerancia';
    case ExposureLevel.HIGH:
      return 'Exposicao frequente proximo aos limites de tolerancia';
    case ExposureLevel.VERY_HIGH:
      return 'Exposicao continua acima dos limites de tolerancia';
  }
}

export const Step1Exposure: React.FC<Step1ExposureProps> = ({ control, errors, disabled }) => (
  <Stack spacing={3}>
    {/* Exposure Level */}
    <Controller
      name="exposureLevel"
      control={control}
      render={({ field }) => (
        <FormControl fullWidth error={!!errors.exposureLevel}>
          <InputLabel>Nivel de Exposicao</InputLabel>
          <Select
            {...field}
            label="Nivel de Exposicao"
            disabled={disabled}
            renderValue={(value) => (
              <ExposureLevelChip level={value as ExposureLevel} selected />
            )}
          >
            {Object.values(ExposureLevel).map((level) => (
              <MenuItem key={level} value={level}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ExposureLevelChip level={level} />
                  <Typography variant="body2" color="text.secondary">
                    {getExposureLevelDescription(level)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          {errors.exposureLevel && (
            <FormHelperText>{errors.exposureLevel.message}</FormHelperText>
          )}
        </FormControl>
      )}
    />

    {/* Start Date */}
    <Controller
      name="startDate"
      control={control}
      render={({ field }) => (
        <DatePicker
          {...field}
          label="Data de inicio da exposicao"
          disabled={disabled}
          maxDate={new Date()}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!errors.startDate,
              helperText:
                errors.startDate?.message ?? 'Quando o trabalhador comecou a ser exposto',
            },
          }}
        />
      )}
    />

    {/* End Date */}
    <Controller
      name="endDate"
      control={control}
      render={({ field }) => (
        <DatePicker
          {...field}
          label="Data de fim da exposicao (opcional)"
          disabled={disabled}
          slotProps={{
            textField: {
              fullWidth: true,
              error: !!errors.endDate,
              helperText:
                errors.endDate?.message ??
                'Deixe em branco se a exposicao ainda esta ativa',
            },
          }}
        />
      )}
    />

    {/* Observations */}
    <Controller
      name="observations"
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          label="Observacoes"
          multiline
          rows={3}
          fullWidth
          error={!!errors.observations}
          helperText={
            errors.observations?.message ??
            `${field.value?.length ?? 0}/2000 caracteres`
          }
          disabled={disabled}
          placeholder="Detalhes sobre a exposicao, medidas de controle, EPIs utilizados..."
        />
      )}
    />

    {/* Info box */}
    <Box
      sx={{
        p: 2,
        bgcolor: 'warning.50',
        borderRadius: 1,
        border: 1,
        borderColor: 'warning.200',
      }}
    >
      <Typography variant="body2" color="warning.800">
        <strong>Importante:</strong> Niveis de exposicao ALTO e MUITO ALTO serao
        destacados nos alertas de saude ocupacional.
      </Typography>
    </Box>
  </Stack>
);

export default Step1Exposure;
